/**
 * ===================================================================
 * REACT HOOK - RESERVOIRS
 * ===================================================================
 * Hook pro spravu nadrzi s React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reservoirService } from '../services/reservoirService';
import type { CreateReservoirDTO, UpdateReservoirDTO } from '../types/reservoirs';

// Query keys
const QUERY_KEYS = {
  reservoirs: ['reservoirs'] as const,
  deviceReservoirs: (deviceUuid: string) => ['reservoirs', 'device', deviceUuid] as const,
  defaultReservoir: (deviceUuid: string) =>
    ['reservoirs', 'device', deviceUuid, 'default'] as const,
  reservoir: (reservoirId: string) => ['reservoirs', reservoirId] as const,
  reservoirSensors: (reservoirId: string) => ['reservoirs', reservoirId, 'sensors'] as const,
};

/**
 * Hook pro ziskani vsech nadrzi zarizeni
 */
export const useDeviceReservoirs = (deviceUuid: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.deviceReservoirs(deviceUuid || ''),
    queryFn: () => reservoirService.getDeviceReservoirs(deviceUuid!),
    enabled: !!deviceUuid,
    staleTime: 30000,
    retry: 1,
  });
};

/**
 * Hook pro ziskani vychozi nadrze zarizeni
 */
export const useDefaultReservoir = (deviceUuid: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.defaultReservoir(deviceUuid || ''),
    queryFn: () => reservoirService.getDefaultReservoir(deviceUuid!),
    enabled: !!deviceUuid,
    staleTime: 30000,
    retry: 1,
  });
};

/**
 * Hook pro ziskani jedne nadrze
 */
export const useReservoir = (reservoirId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.reservoir(reservoirId || ''),
    queryFn: () => reservoirService.getReservoir(reservoirId!),
    enabled: !!reservoirId,
    staleTime: 30000,
    retry: 1,
  });
};

/**
 * Hook pro vytvoreni nove nadrze
 */
export const useCreateReservoir = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReservoirDTO) => reservoirService.createReservoir(data),
    onSuccess: (newReservoir) => {
      // Invaliduj cache pro device reservoirs
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.deviceReservoirs(newReservoir.device_uuid || ''),
      });
    },
  });
};

/**
 * Hook pro aktualizaci nadrze
 */
export const useUpdateReservoir = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reservoirId, data }: { reservoirId: string; data: UpdateReservoirDTO }) =>
      reservoirService.updateReservoir(reservoirId, data),
    onSuccess: (updatedReservoir) => {
      // Aktualizuj cache
      queryClient.setQueryData(QUERY_KEYS.reservoir(updatedReservoir.id), updatedReservoir);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.deviceReservoirs(updatedReservoir.device_uuid || ''),
      });
    },
  });
};

/**
 * Hook pro smazani nadrze
 */
export const useDeleteReservoir = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reservoirId: string) => reservoirService.deleteReservoir(reservoirId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reservoirs });
    },
  });
};

/**
 * Hook pro senzory prirazene k nadrzi
 */
export const useReservoirSensors = (reservoirId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.reservoirSensors(reservoirId || ''),
    queryFn: () => reservoirService.getReservoirSensors(reservoirId!),
    enabled: !!reservoirId,
    staleTime: 30000,
    retry: 1,
  });
};

/**
 * Hook pro prirazeni senzoru k nadrzi
 */
export const useAssignSensor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reservoirId, sensorId }: { reservoirId: string; sensorId: string }) =>
      reservoirService.assignSensor(reservoirId, sensorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.reservoirSensors(variables.reservoirId),
      });
      queryClient.invalidateQueries({ queryKey: ['sensors'] });
    },
  });
};

/**
 * Hook pro odebrani senzoru z nadrze
 */
export const useUnassignSensor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reservoirId, sensorId }: { reservoirId: string; sensorId: string }) =>
      reservoirService.unassignSensor(reservoirId, sensorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.reservoirSensors(variables.reservoirId),
      });
      queryClient.invalidateQueries({ queryKey: ['sensors'] });
    },
  });
};

/**
 * Kombinovany hook pro kompletni spravu nadrzi
 */
export const useReservoirs = (deviceUuid: string | undefined) => {
  const reservoirsQuery = useDeviceReservoirs(deviceUuid);
  const defaultQuery = useDefaultReservoir(deviceUuid);
  const createMutation = useCreateReservoir();
  const updateMutation = useUpdateReservoir();
  const deleteMutation = useDeleteReservoir();

  return {
    // Data
    reservoirs: reservoirsQuery.data || [],
    defaultReservoir: defaultQuery.data,
    isLoading: reservoirsQuery.isLoading,
    error: reservoirsQuery.error,

    // Actions
    refetch: reservoirsQuery.refetch,
    createReservoir: createMutation.mutateAsync,
    updateReservoir: updateMutation.mutateAsync,
    deleteReservoir: deleteMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
